import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import conversationService, {
  type ConversationChannel,
  type ConversationListItem,
} from '@/services/conversationService';
import {
  applyMessageEventToContactList,
  applyTakeoverEventToContactList,
  createSeenEventIds,
  type WhatsAppMessageEvent,
  type WhatsAppTakeoverEvent,
} from '@/lib/liveConversation';
import type { CallLiveEvent } from '@/lib/liveCall';
import { phoneDigits } from '@/lib/conversationFormat';
import { createMutationClock, fetchUnlessOvertaken } from '@/lib/mutationClock';
import { useDegradedPolling, useLiveEvent } from '@/hooks/useLiveEvents';

/** Bursts of events for contacts not yet in the list collapse into one reload. */
export const SILENT_RELOAD_DEBOUNCE_MS = 400;
export const LIST_PAGE_SIZE = 50;

export interface ListFilters {
  channel: ConversationChannel | undefined;
  query: string;
  agentNumber: string;
}

export interface ConversationListState {
  items: ConversationListItem[];
  loading: boolean;
  /** The first load finished (successfully or not). */
  loadedOnce: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * The Verläufe list per contact (`group=contact`, livecenter §5) with live
 * updates: WhatsApp rows are matched by `contact_key` only; a contact or a
 * finished call that is not listed yet triggers one debounced silent reload.
 */
export function useConversationList(
  { channel, query, agentNumber }: ListFilters,
  /** Hold the load while the page is about to change the filters (a pending jump). */
  paused = false,
): ConversationListState {
  const [items, setItems] = useState<ConversationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Background refetches started before the latest local change are stale (mutationClock.ts).
  const [mutations] = useState(createMutationClock);
  // SSE ids already applied, so a replay never counts a message twice.
  const [seen] = useState(() => createSeenEventIds());
  const seqRef = useRef(0);
  const filtersRef = useRef<ListFilters>({ channel, query, agentNumber });
  useLayoutEffect(() => {
    filtersRef.current = { channel, query, agentNumber };
  });
  // The list also lives in a ref that every change writes synchronously: a
  // live event landing between a commit and the passive effects must decide
  // "known contact?" against the list on screen, not the one before it
  // (otherwise it reloads instead of updating the row — seen under load).
  const itemsRef = useRef(items);
  const replaceItems = useCallback((next: ConversationListItem[]) => {
    itemsRef.current = next;
    setItems(next);
  }, []);
  const updateItems = useCallback((change: (prev: ConversationListItem[]) => ConversationListItem[]) => {
    const next = change(itemsRef.current);
    if (next === itemsRef.current) return;
    itemsRef.current = next;
    setItems(next);
  }, []);

  const load = useCallback(
    async (filters: ListFilters, silent: boolean) => {
      const seq = silent ? seqRef.current : ++seqRef.current;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const fetchList = () =>
        conversationService.list(
          filters.channel,
          filters.query,
          LIST_PAGE_SIZE,
          0,
          filters.agentNumber || undefined,
          'contact',
        );
      try {
        const res = silent ? await fetchUnlessOvertaken(mutations, fetchList) : await fetchList();
        if (res === null || seq !== seqRef.current) return;
        replaceItems(res.items || []);
        setError(null);
      } catch {
        // A failed background refresh keeps the list on screen.
        if (silent || seq !== seqRef.current) return;
        setError('Verläufe konnten nicht geladen werden.');
        replaceItems([]);
      } finally {
        if (!silent && seq === seqRef.current) {
          setLoading(false);
          setLoadedOnce(true);
        }
      }
    },
    [mutations, replaceItems],
  );

  useEffect(() => {
    if (paused) return;
    load({ channel, query, agentNumber }, false);
  }, [load, channel, query, agentNumber, paused]);

  const reloadSilently = useCallback(() => {
    load(filtersRef.current, true);
  }, [load]);

  const reload = useCallback(() => {
    load(filtersRef.current, false);
  }, [load]);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleSilentReload = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      reloadSilently();
    }, SILENT_RELOAD_DEBOUNCE_MS);
  }, [reloadSilently]);
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  useLiveEvent<WhatsAppMessageEvent>('whatsapp.message', (event, liveEvent) => {
    if (!event?.message || typeof event.conversation_id !== 'number') return;
    // Checked here, not in the updater (StrictMode runs updaters twice).
    if (!seen.markNew(liveEvent.id)) return;
    mutations.tick();
    const known = itemsRef.current.some((i) => i.type === 'whatsapp' && !!event.contact_key && i.contact_key === event.contact_key);
    if (known) {
      updateItems((prev) => applyMessageEventToContactList(prev, event).items);
      return;
    }
    // A contact not listed yet: reload if it can belong to this view (the
    // server applies search + agent filter; the phone chip never shows WhatsApp).
    const f = filtersRef.current;
    if (f.channel !== 'phone' && (!f.agentNumber || phoneDigits(f.agentNumber) === phoneDigits(event.agent_number))) {
      scheduleSilentReload();
    }
  });

  useLiveEvent<WhatsAppTakeoverEvent>('whatsapp.takeover', (event) => {
    if (!event?.contact_key) return;
    mutations.tick();
    updateItems((prev) => applyTakeoverEventToContactList(prev, event));
  });

  // A finished call gets its stored conversation row: show it.
  useLiveEvent<CallLiveEvent>('call.live', (event) => {
    if (event?.status !== 'ended' || event.conversation_id == null) return;
    if (filtersRef.current.channel === 'whatsapp') return;
    scheduleSilentReload();
  });

  // (Re)connected → one silent refetch covers what the stream could not
  // replay. Stream down → poll every 5 s.
  useLiveEvent<{ last_id?: unknown } | null>('ready', (data) => {
    if (typeof data?.last_id === 'number') seen.noteServerCursor(data.last_id);
    reloadSilently();
  });
  useDegradedPolling(reloadSilently);

  return { items, loading, loadedOnce, error, reload };
}

export default useConversationList;
